import styled from "styled-components";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useUserState } from 'hooks/useUserState';
import { useAnnouncementsState } from 'hooks/useAnnouncementsState';

import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import Tooltip from '@mui/material/Tooltip';

import Announcements from "pages/dashboard/announcements/Announcements";
import AddAnnouncement from "pages/dashboard/announcements/add-announcement";
import Events from "pages/dashboard/events/Events";
import Challenges from "pages/dashboard/challenges/Challenges";
import MYBP from "pages/dashboard/mybp/Mybp";
import { useChallengesState } from 'hooks/useChallengesState';
import { CHALLENGE_STATUS } from 'pages/dashboard/challenges/state/constants';
import { useBudgetState } from 'hooks/useBudgetState';
import { subscribeToEventsExpanded, setEventsExpanded as setGlobalEventsExpanded } from './events/eventsState';

export default function DashboardWall() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [announcementsExpanded, setAnnouncementsExpanded] = useState(false);
  const [eventsExpanded, setLocalEventsExpanded] = useState(false);
  const [myContentExpanded, setMyContentExpanded] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [leftWidth, setLeftWidth] = useState(365);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const { organizations } = useUserState();
  const { updateAnnouncement } = useAnnouncementsState();
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Check if user is admin of any organization
  const isAdmin = organizations?.admin?.length > 0;

  const handleMouseMove = useRef((e) => {
    e.preventDefault();
    const diff = e.clientX - startX.current;
    const newWidth = startWidth.current + diff;
    if (newWidth >= 250 && newWidth <= 600) {
      setLeftWidth(newWidth);
    }
  }).current;

  const handleMouseUp = useRef(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
  }).current;

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    startX.current = e.clientX;
    startWidth.current = leftWidth;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
  };

  useEffect(() => {
    return () => {
      if (isDragging) {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Subscribe to events expanded changes
  useEffect(() => {
    const unsubscribe = subscribeToEventsExpanded((expanded) => {
      setLocalEventsExpanded(expanded);
      if (expanded) {
        setAnnouncementsExpanded(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleAnnouncementsExpandClick = (e) => {
    e.stopPropagation();
    setAnnouncementsExpanded(!announcementsExpanded);
    if (eventsExpanded) {
      setLocalEventsExpanded(false);
      setAnnouncementsExpanded(false);
      return;
    }
    if (!announcementsExpanded) {
      setLocalEventsExpanded(false);
    }
  };

  const handleEventsExpandToggle = (expanded) => {
    setGlobalEventsExpanded(!eventsExpanded);
    if (announcementsExpanded) {
      setAnnouncementsExpanded(false);
    }
  };

  const handleChallengeExpandClick = (e) => {
    e.stopPropagation();
    setMyContentExpanded(!myContentExpanded);
  };

  const handleAddAnnouncementClick = (e) => {
    e.stopPropagation();
    setShowAddAnnouncement(true);
    setAnnouncementsExpanded(true);
    setLocalEventsExpanded(false);
    setExpandedSection('announcements');
  };

  const handleSaveAnnouncement = async (announcementData) => {
    try {
      setIsSaving(true);
      const result = await updateAnnouncement(announcementData);
      if (result.error) throw result.error;
      
      setJustSaved(true);
      setTimeout(() => {
        setShowAddAnnouncement(false);
        setExpandedSection(null);
        setAnnouncementsExpanded(false);
        setIsSaving(false);
        setJustSaved(false);
      }, 1000);
    } catch (error) {
      setIsSaving(false);
      console.error('Failed to save announcement:', error);
      alert('Failed to save announcement. Please try again.');
    }
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      setIsSaving(true);
      const result = await updateAnnouncement({
        id: announcement.id,
        organization_id: announcement.organization_id,
        _delete: true
      });
      
      if (result.error) throw result.error;
      
      setShowAddAnnouncement(false);
      setExpandedSection(null);
      setAnnouncementsExpanded(false);
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      alert('Failed to delete announcement. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardWallContainer>
      <Left style={{ width: `${leftWidth}px` }} $isAdding={showAddAnnouncement || showAddEvent}>
        <AnnouncementsContainer
          $hide={expandedSection === 'events'}
          $expanded={announcementsExpanded}
          $isAdding={showAddAnnouncement}
        >
          <ContainerHeader $isAdding={showAddAnnouncement}>
            {showAddAnnouncement ? (
              <SectionTitle>Add Announcement</SectionTitle>
            ) : (
              <>
                <SectionTitle>Announcements</SectionTitle>
                <ActionButtons>
                  <ExpandIcon fontSize="small" onClick={handleAnnouncementsExpandClick} />
                  {isAdmin && <AddAnnouncementIcon fontSize="small" onClick={handleAddAnnouncementClick} />}
                </ActionButtons>
              </>
            )}
          </ContainerHeader>
          <Announcements
            isHidden={expandedSection === 'events'}
            onExpand={() => setExpandedSection('announcements')}
            onCollapse={() => setExpandedSection(null)}
            isExpanded={announcementsExpanded}
            showAddAnnouncement={showAddAnnouncement}
            onCloseAdd={() => {
              setShowAddAnnouncement(false);
              setExpandedSection(null);
              setAnnouncementsExpanded(false);
            }}
          />
        </AnnouncementsContainer>
        <EventsContainer $hide={expandedSection === 'announcements'} $expanded={eventsExpanded} $isAdding={showAddEvent}>
          <Events
            isHidden={expandedSection === 'announcements'}
            onExpand={() => setExpandedSection('events')}
            onCollapse={() => setExpandedSection(null)}
            sectionTitle={SectionTitle}
            onExpandToggle={handleEventsExpandToggle}
            showAddEvent={showAddEvent}
            setShowAddEvent={setShowAddEvent}
            eventsExpanded={eventsExpanded}
            setEventsExpanded={setLocalEventsExpanded}
          />
        </EventsContainer>
      </Left>
      <DragBar onMouseDown={handleMouseDown} />
      <Right>
        <MyContentContainer
          expanded={myContentExpanded}
          onExpandClick={handleChallengeExpandClick}
        />
        {/* <Resources>Resources */}
          {/* <p><i>Resource Drop In Progress</i></p> */}
        {/* </Resources> */}
      </Right>
      {isDragging && <DragOverlay />}
    </DashboardWallContainer>
  );
}

const DashboardWallContainer = styled.div.attrs({
  className: 'DashboardWallContainer'
})`
  display: flex;
  flex: 1;
  height: 100%;
  width: 100%;
  gap: 0px;
`;

const Left = styled.div.attrs({
  className: 'Left'
})`
  background: ${({ $isAdding }) => $isAdding ? 'white' : '#f1f1f1'};
  display: flex;
  flex-direction: column;
  flex: none;
  border-radius: 5px;
  padding: ${({ $isAdding }) => $isAdding ? '20px 10px 0px' : '20px 15px'};
  
  box-sizing: border-box;
  min-width: 365px;
  transition: width 0.05s ease;
`;

const Right = styled.div.attrs({
  className: 'Right'
})`
  background: #f1f1f1;
  border-radius: 5px;
  box-sizing: border-box;
  display: flex;
  flex: 2;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 10px;
  background: white;
  padding: 0px;
  max-width: none;
  overflow-y: auto;
  overflow-x: hidden;
`;

const WallSection = styled.div.attrs({
  className: 'WallSection'
})`
  flex-direction: column;
  flex: 1;
  font-size: 18px;
  font-weight: 900;
  color: #111111;
  background: ${({ $isAdding }) => $isAdding ? 'white' : '#f1f1f1'};
  border-radius: 5px;
  text-align: left;
  padding: 10px 0px 0px;
  overflow: hidden;
  gap: 10px;
  display: ${({ $hide }) => $hide ? 'none' : 'flex'};
  position: relative;
  &:hover {
    transition: all, 0.3s;
  }
`;

const ContainerHeader = styled.div.attrs({
  className: 'ContainerHeader'
})`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding: 0px 20px 0px 0px;
  // background: ${({ $isAdding }) => $isAdding ? 'white' : 'transparent'};
  background: tranparent;
  &.MyContentHeader {
    padding: 20px 20px 0px 20px;
    // margin-bottom: 15px;
  }
`;

const SectionTitle = styled.div.attrs({
  className: 'SectionTitle'
})`
  padding-left: 7px;
  margin-right: 5px;
  background: transparent;
`;

const ActionButtons = styled.div.attrs({
  className: 'ActionButtons'
})`
  display: flex;
  gap: 10px;
  background: white;
  border-radius: 25px;
  padding: 5px 8px;
  justify-content: flex-end;
  margin-to: -5px;
`;

const ExpandIcon = styled(AspectRatioIcon).attrs({
  className: 'ExpandIcon'
})`
  color: #B2BEB5;
  z-index: 2;
  transition: all 0.3s;
  &:hover {
    color: #000;
    &.dark {
      color: #1CCCA4;
    }
  }
`;

const AddAnnouncementIcon = styled(ControlPointDuplicateIcon).attrs({
  className: 'AddAnnouncementIcon'
})`
  color: #B2BEB5;
  &:hover {
    color: #000;
    transition: all 0.3s;
  }
`;

const AnnouncementsContainer = styled(WallSection).attrs({
  className: 'AnnouncementsContainer'
})`
  flex: ${({ $expanded }) => $expanded ? 8 : 1};
  transition: flex 0.3s ease;
  min-height: 35px;
  background: ${({ $isAdding }) => $isAdding ? 'white' : '#f1f1f1'};

  > div:not(.ContainerHeader) {
    transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: opacity;
  }

  ${({ $isAdding }) => $isAdding && `
    > div:not(.ContainerHeader) {
      animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    }

    @keyframes fadeIn {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }
  `}
`;

const EventsContainer = styled(WallSection).attrs({
  className: 'EventsContainer'
})`
  position: relative;
  flex: ${({ $expanded }) => $expanded ? 14 : 1};
  transition: flex 0.3s ease;
  min-height: 62px;
  box-shadow: none;
`;

const News = styled(WallSection).attrs({
  className: 'News'
})``;

const Resources = styled(WallSection).attrs({
  className: 'Resources'
})`
  padding: 28px 23px;
  display: ${({ $hide }) => $hide ? 'none' : 'flex'};
  flex-direction: column;
  flex: 1;
  transition: flex 0.3s ease;
`;

const MyContentContainer = ({ expanded, onExpandClick }) => {
  const { state: challengesState, addChallengeInstance, loading: challengesLoading } = useChallengesState();
  const { budgetState, addRevenueCategory } = useBudgetState();
  const [activeTab, setActiveTab] = useState('revenue');
  const [showArchived, setShowArchived] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    // console.log('DashboardWall - Challenges State:', challengesState);
  }, [challengesState]);

  const hasChallenges = useMemo(() => {
    // console.log('DashboardWall - Calculating hasChallenges:', challengesState.challenges?.instances);
    const result = Object.values(challengesState.challenges?.instances || {})
      .filter(challenge => !challenge.is_archived)
      .length > 0;
    // console.log('DashboardWall - Has Challenges?', result);
    return result;
  }, [challengesState.challenges?.instances]);
  
  const hasNoBudget = budgetState.categories.length === 0;

  const handleTabChange = (newTab) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(newTab);
      setIsTransitioning(false);
    }, 125);
  };

  const handleAddClick = () => {
    if (activeTab === 'challenges') {
      setShowArchived(false);
      const newChallenge = {
        name: 'New Challenge',
        status: CHALLENGE_STATUS.NEW,
        created_at: new Date().toISOString(),
        category_id: 'uncategorized',
        team_id: challengesState.teamId,
        is_active: true
      };
      addChallengeInstance(newChallenge);
    } else {
      addRevenueCategory();
    }
  };

  return (
    <MyContentContainerWrapper $expanded={expanded}>
      <ContainerHeader className="MyContentHeader">
        <TabsContainer>
    
          <Tab
            $active={activeTab === 'revenue'}
            onClick={() => handleTabChange('revenue')}
          >
            Revenue
          </Tab>
          <Tab
            $active={activeTab === 'challenges'}
            // onClick={() => handleTabChange('challenges')}
            $disabled={true}
          >
            <span>Challenges</span>
            <span className="meta">Coming Soon</span>
          </Tab>
        </TabsContainer>
      </ContainerHeader>

      <TabContent $active={!isTransitioning} $expanded={expanded}>
        {activeTab === 'challenges' ? (
          <Challenges showArchived={showArchived} />
        ) : (
          <MYBP isEmpty={hasNoBudget} />
        )}
      </TabContent>

      <MyContentBottomBar>
        <ButtonGroup>
          <AddButton
            onClick={handleAddClick}
            $shouldPulse={(activeTab === 'challenges' && !hasChallenges) || (activeTab === 'revenue' && hasNoBudget)}
          >
            {activeTab === 'challenges' ? 'New Challenge' : 'New Category'}
          </AddButton>
          
          {activeTab === 'challenges' && (
            <ArchiveToggle onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? 'Hide Archive' : 'Show Archive?'}
            </ArchiveToggle>
          )}
        </ButtonGroup>
        <PrivacySpan>
          <Tooltip
            className="PrivacyTipIcon"
            placement="left"
            arrow
            title={
              <PrivacyContainer>
                <PrivacyHeader>
                  <PrivacyIcon />
                  <PrivacyTitle>Privacy: strong</PrivacyTitle>
                </PrivacyHeader>
                <PrivacyText>Only you can access or share your content.
                  Outcomes & metrics are tracked as meta-data.</PrivacyText>
              </PrivacyContainer>
            }
          >
            <PrivacyIcon />
          </Tooltip>
        </PrivacySpan>
        <ExpandIcon className="dark" fontSize="small" onClick={onExpandClick} />
      </MyContentBottomBar>
    </MyContentContainerWrapper>
  );
};

const MyContentContainerWrapper = styled(WallSection).attrs({
  className: 'MyContentContainerWrapper'
})`
  display: flex;
  flex-direction: column;
  flex: ${({ $expanded }) => $expanded ? 19 : 3};
  padding: 10px 20px;
  background: black;
  color: white;
  transition: flex 0.3s ease;
  gap: 13px;
  min-height: 100px;
  ${({ $expanded }) => $expanded && `
    .DropdownContent {
      height: 900%;
      // height: fit-content;
      padding-bottom: 20px;
      transition: all 0.2s ease-in-out;
    }
  `}
`;

const TabsContainer = styled.div.attrs({
  className: 'TabsContainer'
})`
  display: flex;
  gap: 20px;
`;

const Tab = styled.div.attrs({
  className: 'Tab'
})`
  font-size: 21px;
  font-weight: 900;
  color: ${props => props.$active ? 'white' : '#666'};
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  position: relative;
  ${props => props.$disabled && `
    opacity: 0.5;
    cursor: default;
    &:hover {
      color: '#666'};
    }
  `}
  .meta {
    font-size: 10px;
    color: #1CCCA4;
    position: absolute;
    top: 33%;
    right: -60px;
    font-style: italic;
    }
  
  &:hover {
    // color: ${props => props.$active ? 'white' : '#999'};
  }
`;

const MyContentBottomBar = styled.div.attrs({
  className: 'MyContentBottomBar'
})`
  background: #1a1a1a;
  height: 50px;
  display: flex;
  flex-direction: row;
  flex: 1;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  bottom: -0px;
  left: 0;
  z-index: 2;
  gap: 15px;
  padding: 0px 40px 0px 20px;
  width: 100%;
  box-sizing: border-box;
`;

const AddButton = styled.button.attrs({
  className: 'AddButton'
})`
  align-self: center;
  padding: 7px 12px 7px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 2px;
  background: ${props => props.children === 'New Category' ? '#1CCCA4' : '#5000d0'};
  color: white;
  border: none;
  cursor: pointer;
  margin-right: auto;
  font-family: 'PT Sans', sans-serif;
  animation: ${props => props.$shouldPulse ? 'pulse 2s ease-out infinite' : 'none'};

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.2);
      font-weight: 900;
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
      font-weight: 700;
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
      font-weight: 900;
    }
  }

  &:hover {
    background: ${props => props.children === 'New Category' ? '#19b894' : '#4400b3'};
    transition: background 0.3s ease;
    animation: none;
  }
`;

const PrivacySpan = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px;
  font-family: 'PT Sans', sans-serif;
`;

const PrivacyIcon = styled(PrivacyTipIcon)`
  color: #B2BEB5;
  transition: all 0.3s;
  transform: scale(0.9);
  &:hover {
    color: white; 
  }
`;

const Live = styled(WallSection).attrs({
  className: 'Live'
})``;

const PrivacyContainer = styled.div.attrs({
  className: 'PrivacyContainer'
})`
  display: flex;
  flex-direction: column;
  padding: 10px 5px;
`;

const PrivacyHeader = styled.div.attrs({
  className: 'PrivacyHeader'
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 5px;
  svg {
    font-size: 19px;
  }
`;

const PrivacyTitle = styled.span.attrs({
  className: 'PrivacyTitle'
})`
  font-weight: bold;
`;

const PrivacyText = styled.span.attrs({
  className: 'PrivacyText'
})`
  color: inherit;
  line-height: 14px;
`;

const TabContent = styled.div.attrs({
  className: 'TabContent'
})`
  opacity: 1;
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: ${props => props.$expanded ? '80%' : '70%'};
  position: relative;
  z-index: 2;
`;

const DragBar = styled.div.attrs({
  className: 'DragBar'
})`
  width: 8px;
  margin: 0 4px;
  position: relative;
  cursor: col-resize;
  z-index: 10;
  user-select: none;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 4px;
    top: 5px;
    bottom: 0;
    height: 50%;
    transform: translateX(-50%);
    transition: background 0.2s;
    border-radius: 10px;
    background: #f1f1f1;
    height: 20%;
  }
  
  &:hover::after {
    background: #1CCCA4;
    // height: 50%;
    transition: height 0.2s;
  }
  
  &:active::after {
    background: #19b894;
  }
`;

const DragOverlay = styled.div.attrs({
  className: 'DragOverlay'
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  cursor: col-resize;
  user-select: none;
`;

const ButtonGroup = styled.div.attrs({
  className: 'ButtonGroup'
})`
  display: flex;
  gap: 5px;
  margin-right: auto;
`;

const ArchiveToggle = styled.button.attrs({
  className: 'ArchiveToggle'
})`
  align-self: center;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 3px;
  color: #999;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: none;
  text-decoration: underline;
  width: 120px;
  text-align: center;
  &:hover {
    color: white;
    cursor: default;
  }
`;